/**
 * Twilio SMS Service
 * SMS OTP 발송 및 관리를 위한 클라이언트 라이브러리
 */

import { supabase } from "./supabase";

const API_BASE = import.meta.env.VITE_PROXY_API_SERVER;

class TwilioService {
  /**
   * OTP 발송
   * @param {string} phoneNumber - 전화번호 (E.164 형식)
   * @param {string} type - 'phone' 또는 'email'
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async sendOTP(phoneNumber, type = "phone") {
    try {
      // 현재 사용자 정보 가져오기
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // 만료 시간 설정 (1분)
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 1);

      // 기존 미사용 OTP 삭제
      if (user) {
        await supabase
          .from("otps")
          .delete()
          .eq("user_id", user.id)
          .eq("type", type)
          .eq("value", phoneNumber)
          .eq("verified", false);
      }

      // Twilio API 호출 (서버에서 OTP 생성하고 SMS 발송)
      const response = await fetch(`${API_BASE}/api/v1/twilio/send-sms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: phoneNumber,
          userId: user?.id || null, // 사용자 ID 전달
          generateOtp: true, // 서버에서 OTP 생성 요청
        }),
      });

      // 응답 텍스트를 먼저 가져와서 확인
      const responseText = await response.text();
      // console.log("Server response:", responseText);

      if (!response.ok) {
        let errorMessage = "Failed to send SMS";
        try {
          const error = JSON.parse(responseText);
          errorMessage = error.message || errorMessage;
        } catch (e) {
          console.error("Failed to parse error response:", responseText);
        }
        throw new Error(errorMessage);
      }

      // JSON 파싱 시도
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error("Failed to parse response:", responseText);
        throw new Error("Invalid response from server");
      }

      // 서버에서 OTP를 저장했으므로 클라이언트는 결과만 반환
      return {
        success: true,
        otpId: result.otpId,
        expiresAt: expiresAt.toISOString(),
      };
    } catch (error) {
      console.error("Failed to send SMS:", error);
      return {
        success: false,
        error: error.message || "Failed to send SMS",
      };
    }
  }

  /**
   * OTP 검증
   * @param {string} phoneNumber - 전화번호
   * @param {string} code - OTP 코드
   * @param {string} type - 'phone' 또는 'email'
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async verifyOTP(phoneNumber, code, type = "phone") {
    try {
      // 현재 사용자 정보
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // OTP 레코드 조회
      let query = supabase
        .from("otps")
        .select("*")
        .eq("type", type)
        .eq("value", phoneNumber)
        .eq("code", code)
        .eq("verified", false)
        .gte("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1);

      // 로그인된 사용자의 경우 user_id도 확인
      if (user) {
        query = query.eq("user_id", user.id);
      }

      const { data: otpData, error: fetchError } = await query.single();

      if (fetchError || !otpData) {
        // 시도 횟수 증가
        await this.incrementAttempts(phoneNumber, type);

        return {
          success: false,
          error: "Invalid or expired verification code",
        };
      }

      // 시도 횟수 확인
      if (otpData.attempts >= 5) {
        return {
          success: false,
          error: "Too many attempts. Please request a new code.",
        };
      }

      // OTP를 검증됨으로 표시
      const { error: updateError } = await supabase
        .from("otps")
        .update({
          verified: true,
          attempts: otpData.attempts + 1,
        })
        .eq("id", otpData.id);

      if (updateError) {
        console.error("Failed to update OTP:", updateError);
        throw new Error("Failed to verify OTP");
      }

      // 사용자 메타데이터 업데이트 (전화번호 인증 완료)
      if (user) {
        await supabase.auth.updateUser({
          data: {
            phone: phoneNumber,
            phoneVerified: true,
            phoneVerifiedAt: new Date().toISOString(),
          },
        });
      }

      return {
        success: true,
        message: "Phone number verified successfully",
      };
    } catch (error) {
      console.error("Failed to verify OTP:", error);
      return {
        success: false,
        error: error.message || "Failed to verify OTP",
      };
    }
  }

  /**
   * 시도 횟수 증가
   * @private
   */
  async incrementAttempts(phoneNumber, type) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      let query = supabase
        .from("otps")
        .select("id, attempts")
        .eq("type", type)
        .eq("value", phoneNumber)
        .eq("verified", false)
        .order("created_at", { ascending: false })
        .limit(1);

      if (user) {
        query = query.eq("user_id", user.id);
      }

      const { data: otpData } = await query.single();

      if (otpData) {
        await supabase
          .from("otps")
          .update({ attempts: otpData.attempts + 1 })
          .eq("id", otpData.id);
      }
    } catch (error) {
      console.error("Failed to update attempts:", error);
    }
  }

  /**
   * OTP 재발송
   * @param {string} phoneNumber - 전화번호
   * @param {string} type - 'phone' 또는 'email'
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async resendOTP(phoneNumber, type = "phone") {
    try {
      // 기존 OTP 무효화
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        await supabase
          .from("otps")
          .delete()
          .eq("user_id", user.id)
          .eq("type", type)
          .eq("value", phoneNumber)
          .eq("verified", false);
      }

      // 새 OTP 발송
      return await this.sendOTP(phoneNumber, type);
    } catch (error) {
      console.error("Failed to resend OTP:", error);
      return {
        success: false,
        error: error.message || "Failed to resend OTP",
      };
    }
  }

  /**
   * 만료된 OTP 정리 (관리용)
   */
  async cleanupExpiredOTPs() {
    try {
      const { error } = await supabase
        .from("otps")
        .delete()
        .lt("expires_at", new Date().toISOString());

      if (error) {
        console.error("Failed to cleanup expired OTPs:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Failed to cleanup OTPs:", error);
      return false;
    }
  }
}

export const twilioService = new TwilioService();
